import { useState, useContext, useEffect } from 'react';
import './CardEnrollment.css';
import { CardElementData } from '@adyen/adyen-web/dist/types/components/Card/types';
import { AppContext } from '../context';
import { initiateFSCP } from '../lib/paymentService';
import { Colors, PARAMS, wait } from '../lib/util';
import Balls from '../components/Balls';
import { ERRORS, mapErrorMessage } from '../lib/errors';
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import Main from '../components/Main/Main';
import Header from '../components/Header/Header';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import HeaderBox from '../components/HeaderBox/HeaderBox';
import SubHeader from '../components/SubHeader/SubHeader';
import './CheckoutCardEntry.css';
import CardHandler from './CardHandler';
import { getEpaymentDetails, PaymentOrderDetails } from '../lib/epayment';
import PoweredByVipps from '../components/PoweredByVipps/PoweredByVipps';
import AdyenCore from '@adyen/adyen-web/dist/types/core';
import { CancelLink } from '../components/CancelLink/CancelLink';
import ErrorLogo from '../components/ErrorLogo/ErrorLogo';

export default function CheckoutCardEntry(): JSX.Element {
  const [cardData, setCardData] = useState<null | CardElementData>(null);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [loading, setLoading] = useState(false);
  const context = useContext(AppContext);
  const [threeDSData, setThreeDSData] = useState<PaymentAction | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<PaymentOrderDetails | null>(null);
  const [adyenSdk, setAdyenSdk] = useState<AdyenCore>();
  const [paymentExpired, setPaymentExpired] = useState<boolean>(false);

  const handleCardData = (data: CardElementData) => {
    setCardData(data);
  };
  const onRegisterHandler = async () => {
    if (cardData == null || paymentOrder == null) {
      setErrorMessage(mapErrorMessage(context, ERRORS.INVALID_INPUT));
    } else {
      setErrorMessage('');
      setLoading(true);
      const [err, response] = await Promise.all([
        initiateFSCP(context, cardData, paymentOrder.peToken),
        wait(1000),
      ]).then(([v]) => v);
      if (err) {
        setErrorMessage(mapErrorMessage(context, err));
      } else if (response.adyenData.resultCode.toLowerCase() === 'authenticationfinished') {
        window.location.assign(
          `${context.host}/checkout/auth?${PARAMS.PAYMENT_TOKEN}=${response.adyenData.verificationToken}&${PARAMS.TOKEN}=${context.peApiAuth}&${PARAMS.CARD_PAGE_TOKEN}=${context.epaymentsApiAuth}`
        );
      } else if (!response?.adyenData?.action) {
        setErrorMessage(mapErrorMessage(context, new Error(ERRORS.NETWORK_ISSUE)));
      } else {
        setThreeDSData(response.adyenData.action as PaymentAction);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && !paymentOrder && !errorMessage) {
      setLoading(true);
      Promise.all([getEpaymentDetails(context), wait(1000)]).then(([[err, response]]) => {
        if (err) {
          setErrorMessage(mapErrorMessage(context, err));
        } else {
          setPaymentOrder(response);
        }
        setLoading(false);
      });
    }
    if (!adyenSdk) {
      context.fcspAdyenSdk().then((sdk) => setAdyenSdk(sdk));
    }
  });

  useEffect(() => {
    if (paymentOrder) {
      if (paymentOrder.expireTime > 0) {
        const ptr = setTimeout(() => setPaymentExpired(true), paymentOrder.expireTime);
        return () => clearTimeout(ptr);
      } else setPaymentExpired(true);
    }
  }, [paymentOrder]);

  if (paymentExpired) {
    return (
      <Main footer={<PoweredByVipps />} className="checkout-card-entry">
        <div className="fill error"></div>
        <div className="error-logo">
          <ErrorLogo />
        </div>
        <HeaderBox>
          <div className="titles">
            <Header>{context.texts['fscp.error.timeout']}</Header>
            <SubHeader>{context.texts['fscp.error.message.timeout']}</SubHeader>
          </div>
        </HeaderBox>
        <div className="fill two"></div>
        <PrimaryButton
          color={Colors.Purple}
          loading={false}
          disabled={false}
          onClick={() => window.location.assign(paymentOrder?.returnUrl ?? '')}
        >
          OK
        </PrimaryButton>
      </Main>
    );
  }

  if (loading || !adyenSdk) {
    return (
      <Main footer={<PoweredByVipps />} className="checkout-card-entry">
        <div className="fill"></div>
        <Balls color={Colors.Purple} />
        <div className="fill"></div>
      </Main>
    );
  }

  if (paymentOrder == null) {
    return (
      <Main footer={<PoweredByVipps />} className="checkout-card-entry">
        <p>{errorMessage}</p>
      </Main>
    );
  }

  return (
    <>
      <Main
        footer={
          <>
            <div className="cancel">
              <CancelLink returnUrl={paymentOrder.returnUrl} setErrorMessage={setErrorMessage}>
                {context.texts.checkoutCancel}
              </CancelLink>
            </div>
            <PoweredByVipps />
          </>
        }
        className="checkout-card-entry"
      >
        <HeaderBox>
          <div className="titles">
            <Header>{context.texts.checkoutTitle}</Header>
            <SubHeader>
              {context.texts.checkoutHeader
                .replace('{amount}', paymentOrder.amount.displayValue)
                .replace('{merchant}', paymentOrder.merchantName)}
            </SubHeader>
          </div>
        </HeaderBox>
        <div className="fill one"></div>

        <div className={`card-entry-error-msg ${errorMessage && 'visible'}`} role="alert">
          <p>{errorMessage}</p>
        </div>
        <div className="card-entry">
          <CardHandler
            handleCardData={handleCardData}
            threeDSData={threeDSData}
            adyenSdk={adyenSdk}
          />
        </div>
        <div className="fill two"></div>
        <PrimaryButton color={Colors.Purple} loading={loading} onClick={onRegisterHandler}>
          {context.texts.checkoutPay}
        </PrimaryButton>
      </Main>
    </>
  );
}
